import { Contact } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { isContactPickerSupported } from '@/lib/contact-picker';

/**
 * Minimal typing for the browser Contact Picker API.
 *
 * The Contact Picker API (https://developer.mozilla.org/docs/Web/API/Contact_Picker_API)
 * is only available on Chromium-based browsers on Android. It is not part of the
 * standard TypeScript DOM lib, so we declare the slice we use here. We deliberately
 * read nothing back to the server — picked names and phones are written straight into
 * the form fields and the device's contact list is never stored or transmitted.
 */
interface ContactInfo {
  name?: string[];
  tel?: string[];
}

interface ContactsManager {
  select(
    properties: Array<'name' | 'tel' | 'email' | 'address' | 'icon'>,
    options?: { multiple?: boolean },
  ): Promise<ContactInfo[]>;
}

interface NavigatorWithContacts extends Navigator {
  contacts: ContactsManager;
}

export interface PickedContact {
  name: string;
  phone: string;
}

interface Props {
  /** Called with every selected contact that has at least a name or phone. */
  onPick: (contacts: PickedContact[]) => void;
  label?: string;
}

/**
 * Progressive-enhancement button that opens the device phone book and lets the user
 * select MULTIPLE contacts at once to populate a bulk guest list. Renders nothing on
 * browsers without the Contact Picker API (desktop, iOS Safari), where users keep
 * adding rows manually. Nothing is stored or sent — fill-only, mirroring the mobile
 * apps and the single-invite DeviceContactButton.
 */
export function DeviceContactMultiButton({ onPick, label = 'Add from contacts' }: Props) {
  const [busy, setBusy] = useState(false);

  if (!isContactPickerSupported()) return null;

  const handleClick = async () => {
    setBusy(true);
    try {
      const results = await (navigator as NavigatorWithContacts).contacts.select(['name', 'tel'], {
        multiple: true,
      });
      const picked: PickedContact[] = results
        .map((c) => ({
          name: c.name?.[0]?.trim() ?? '',
          phone: c.tel?.[0]?.trim() ?? '',
        }))
        .filter((c) => c.name !== '' || c.phone !== '');
      if (picked.length > 0) onPick(picked);
    } catch {
      // User cancelled the picker or permission was denied — nothing to do.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={busy}
      onClick={() => void handleClick()}
    >
      <Contact className="size-4" />
      {label}
    </Button>
  );
}
